import {
  includesFullWord,
  looksLikeGeneralArea,
  maybeTransformEmptyRichTextContentToNull,
} from 'shared/utils';
import { formatLocalDateTimeWithoutTimezone } from 'shared/dates';
import {
  WildfireCreateUpdateData,
  Wildfire,
} from 'features/wildfires/wildfire.types';
import { TFunction } from 'i18next';
import { getReportMedia } from 'components/MediaForm';
import { normalizeLinkValues } from 'features/geoEvents/geoEvent.utils';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import { NotificationType } from 'features/notifications/notifications.types';
import { buildReporterNotesPayload } from 'features/reporterNotes/reporterNotes.utils';
import { WildfireFormValues } from './WildfireForm.types';

export const geoEventFormValuesToData = (
  values: WildfireFormValues,
  mediaUrl?: string,
  isCriticalInitialReport = false,
): WildfireCreateUpdateData => {
  const releasePendingZoneUids = values.releasePendingZoneUids?.length
    ? values.releasePendingZoneUids
    : undefined;

  const geoEventData: WildfireCreateUpdateData = {
    name: values.name,
    address: values.isComplexParent ? '' : values.address,
    regions:
      values.isComplexParent && values.childWildfires.length
        ? []
        : values.regions,
    isActive: values.status === 'active',
    notificationType: isCriticalInitialReport
      ? NotificationType.Critical
      : values.notificationType,
    geoEventType: 'wildfire',
    lat: values.lat,
    lng: values.lng,
    reporterManaged: values.reporterManaged,
    data: {
      geoEventType: 'wildfire',
      acreage: values.acreage,
      containment: values.containment,
      isComplexParent: values.isComplexParent,
      isFps: values.isFps,
      isPrescribed: values.isPrescribed,
      evacuationNotes: maybeTransformEmptyRichTextContentToNull(
        values.evacuationNotes,
      ),
      temporarilyDisplayEvacZones: values.temporarilyDisplayEvacZones,
      hasCustomEvacuationAdvisories: values.customAdvisories,
      hasCustomEvacuationOrders: values.customOrders,
      hasCustomEvacuationWarnings: values.customWarnings,
      hasCustomEvacuationShelterInPlace: values.customShelterInPlace,
      evacuationOrders: maybeTransformEmptyRichTextContentToNull(
        values.evacuationOrders,
      ),
      evacuationWarnings: maybeTransformEmptyRichTextContentToNull(
        values.evacuationWarnings,
      ),
      evacuationAdvisories: maybeTransformEmptyRichTextContentToNull(
        values.evacuationAdvisories,
      ),
      evacuationShelterInPlace: maybeTransformEmptyRichTextContentToNull(
        values.evacuationShelterInPlace,
      ),
      links: normalizeLinkValues(values.links),
      reporterOnlyNotes: maybeTransformEmptyRichTextContentToNull(
        values.reporterOnlyNotes,
      ),
      reporterOnlyNotesDateModified: null,
      manualDeactivationStarted: values.manualDeactivationStarted,
      internalLinks: normalizeLinkValues(values.internalLinks),
      urgency: values.urgency,
      unstructuredEvacuationRegionIds: values.unstructuredEvacuationRegionIds,
      // Not editable in this form.
      botLockedFields: null,
    },
    evacZoneStatuses: values.evacZoneStatuses.map((eZS) => ({
      status: eZS.status,
      evacZone: { id: eZS.evacZone.id },
    })),
    childGeoEvents: [
      ...(values.childWildfires.map((ge) => ({
        id: ge.id,
        geoEventType: ge.geoEventType,
      })) || []),
      ...(values.childLocations.map((ge) => ({
        id: ge.id,
        geoEventType: ge.geoEventType,
      })) || []),
    ],
    releasePendingZoneUids,
    reporterNotes: buildReporterNotesPayload(values),
  };

  // Add external geo event association if provided
  if (values.externalGeoEventId) {
    geoEventData.externalGeoEventId = values.externalGeoEventId;
  }

  if (values.prescribedDateStartLocal === null) {
    // explicitly unset prescribedDateStartLocal if it was changed to null
    geoEventData.data.prescribedDateStartLocal = null;
  } else if (values.prescribedDateStartLocal) {
    geoEventData.data.prescribedDateStartLocal =
      formatLocalDateTimeWithoutTimezone(values.prescribedDateStartLocal);
  }
  const newMediaFile = values.media;
  const { provisionalSubmissionMediaId } = values;
  const submissionHasReusableMedia =
    !newMediaFile &&
    Boolean(values.mediaUrl) &&
    provisionalSubmissionMediaId != null;

  if (
    values.reportMessage ||
    values.embedUrl ||
    newMediaFile ||
    submissionHasReusableMedia
  ) {
    geoEventData.initialReport = {};
  }
  if (values.reportMessage) {
    geoEventData.initialReport!.message = values.reportMessage;
  }
  if (values.embedUrl) {
    geoEventData.initialReport!.embedUrl = values.embedUrl;
  }
  if (newMediaFile) {
    geoEventData.initialReport!.media = getReportMedia({
      media: newMediaFile,
      fileType: values.fileType,
      lat: values.mediaLat,
      lng: values.mediaLng,
      az: values.mediaAz,
      mediaUrl,
    });
  } else if (submissionHasReusableMedia) {
    geoEventData.initialReport!.media = [{ id: provisionalSubmissionMediaId! }];
  }

  return geoEventData;
};

export const getSubmitButtonKey = ({
  isProvisional,
  isEditing,
  isScheduled,
  isCritical,
}: {
  isProvisional: boolean;
  isEditing: boolean;
  isScheduled: boolean;
  isCritical: boolean;
}): string => {
  if (isProvisional) return 'createEditWildfire.buttons.createProvisional';
  if (isEditing) return 'createEditWildfire.buttons.save';
  if (isCritical) return 'createEditWildfire.buttons.createCritical';
  if (isScheduled) return 'createEditWildfire.buttons.schedule';
  return 'createEditWildfire.buttons.create';
};

export const validateIncidentName = (
  incidentName: string,
  t: TFunction,
): string | null => {
  if (!incidentName) return null;

  const validKeywords = ['fire', 'prescribed', 'burn', 'complex'];

  if (!includesFullWord(incidentName, validKeywords)) {
    return t('createEditWildfire.inputs.name.warning');
  }

  return null;
};

export const validateIncidentAddress = (
  address: string,
  t: TFunction,
): string | null => {
  if (!looksLikeGeneralArea(address)) {
    return t('createEditWildfire.inputs.address.warning');
  }

  return null;
};

export const validatePrescribedIncident = (
  incidentName: string,
  isPrescribed: boolean,
  t: TFunction,
): string | null => {
  if (!incidentName) return null;

  if (!isPrescribed && includesFullWord(incidentName, ['prescribed'])) {
    return t('createEditWildfire.prescribedBurnWarning');
  }

  return null;
};

export const validateComplexIncidentName = (
  incidentName: string,
  isComplexParent: boolean,
  t: TFunction,
): string | null => {
  if (!incidentName) return null;

  if (!isComplexParent && includesFullWord(incidentName, ['complex'])) {
    return t('createEditWildfire.complexNameWarning');
  }

  return null;
};

// For complex parents, `formRegions` is empty when child geo events exist, and otherwise
// defaults to the incident's own regions.
// Since both cases result in the same behavior, we simply check `isComplexParent`
// and use the wildfire's regions.
export const getWildfireRegionsForEvacZones = (
  formRegions: GeoEventRegion[],
  wildfire?: Wildfire,
): GeoEventRegion[] => {
  return wildfire?.data.isComplexParent
    ? (wildfire.regions ?? [])
    : formRegions;
};
